import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import { getCurrentUserWithETag, updateUserProfile, UserProfile } from '../services/api/users';
import { useAuth } from './AuthContext';
import { getErrorMessage } from '../utils/errorUtils';
import { cacheGet, cacheSet, setCacheUserId, clearCacheUserId, cacheDeleteDatabase } from '../services/cache';
import { cacheKeys } from '../services/cache/cacheKeys';
import { clearThumbnailServiceWorkerCache } from '../services/serviceWorkerRegistration';
import { imageLoaderService } from '../services/imageLoaderService';

interface UserContextType {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
  updateDisplayName: (displayName: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Default context value for when used outside provider (e.g., in tests)
const defaultContextValue: UserContextType = {
  user: null,
  loading: false,
  error: null,
  updateDisplayName: async () => {},
  refreshUser: async () => {},
};

const UserContext = createContext<UserContextType>(defaultContextValue);

export const useUser = (): UserContextType => {
  return useContext(UserContext);
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to load from IndexedDB cache first for instant display, and capture
      // the cached ETag so we can revalidate against the server with If-None-Match.
      const key = cacheKeys.userProfile();
      let cachedEtag: string | null = null;
      try {
        const cached = await cacheGet<UserProfile>(key);
        if (cached?.data) {
          setUser(cached.data);
          // Initialize cache scoping with the cached user ID
          setCacheUserId(cached.data.id);
          cachedEtag = cached.etag;
        }
      } catch {
        // Cache read failed — continue
      }

      // Always revalidate against the API. A 304 means our cached data is still
      // good; only update local state if the server returned fresh data.
      const response = await getCurrentUserWithETag(cachedEtag);

      if (response.notModified) {
        // Cached data is still fresh — just refresh the cache timestamp via cacheSet.
        // No-op for state.
        return;
      }

      if (response.data) {
        setUser(response.data);
        setCacheUserId(response.data.id);
        // Persist the fresh data along with the new ETag for future revalidation.
        cacheSet(key, response.data, response.etag).catch(() => {});
      }
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to load user profile');
      setError(message);
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateDisplayName = useCallback(async (displayName: string) => {
    try {
      setError(null);
      const updatedUser = await updateUserProfile({ displayName });
      setUser(updatedUser);
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to update display name');
      setError(message);
      throw err;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  // Only fetch user profile when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
    } else {
      setUser(null);
      setLoading(false);
      setError(null);
      // Clear cache on logout
      cacheDeleteDatabase().catch(() => {});
      clearThumbnailServiceWorkerCache().catch(() => {});
      // Drop in-memory blob URLs (releases memory and prevents the next
      // signed-in user from seeing leftover image data on this tab).
      imageLoaderService.cleanup();
      clearCacheUserId();
    }
  }, [isAuthenticated, fetchUser]);

  const value: UserContextType = {
    user,
    loading,
    error,
    updateDisplayName,
    refreshUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
